import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/auth';
import { AngularFirestore, QueryFn } from '@angular/fire/firestore';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import {
  Ativacao,
  Cliente,
  Empresa,
  LojaDeduplicada,
  Notas,
  VendCRM,
  VendedorInfo,
} from '../../../models/models';
import { AtivacoesService } from '../../ativacoes.service';
import { SecurityService } from '../../security.service';
import { GenericFirebaseRequestsService } from './generic-firebase-requests.service';

@Injectable({
  providedIn: 'root',
})
export class FirebaseRequestsService {
  currentVendedorInfo!: VendedorInfo;

  private vendedorAtualSubject = new BehaviorSubject<VendedorInfo | null>(null);
  vendedorAtual$ = this.vendedorAtualSubject.asObservable();

  constructor(
    private genericFireReqs: GenericFirebaseRequestsService,
    private security: SecurityService,
    private ativacaoService: AtivacoesService,
    private firestore: AngularFirestore,
    private spinner: NgxSpinnerService,
    private auth: AngularFireAuth
  ) { }

  private getNotas(
    clienteCpf: string,
    clienteSiglaMarca: string,
    getDataOnce = false,
    loading = true
  ): Observable<Notas> {
    return this.genericFireReqs
      .getDocument<Notas>(
        environment.DB_notas_clientes,
        `${clienteSiglaMarca}_${clienteCpf}:base`,
        getDataOnce,
        loading
      )
      .pipe(
        map((notas) => {
          if (notas) {
            notas?.notas?.sort(function (a, b) {
              return (
                (b.data as unknown as firebase.firestore.Timestamp)
                  .toDate()
                  .getTime() -
                (a.data as unknown as firebase.firestore.Timestamp)
                  .toDate()
                  .getTime()
              );
            });

            console.log(notas);
            return notas;
          } else {
            return {} as Notas;
          }
        })
      );
  }

  getClientesDoVendedorComLembretes(
    clienteNota: any,
    limit: number
  ): Observable<Notas[]> {
    const queryFn: QueryFn = clienteNota
      ? (ref) =>
        ref
          .where(
            'id_vendedor_preferencia',
            '==',
            this.security.currentUserValue.id_vendedor
          )
          .where('possui_lembretes', '==', true)
          .orderBy('cpf')
          .startAfter(clienteNota.cpf)
          .limit(limit)
      : (ref) =>
        ref
          .where(
            'id_vendedor_preferencia',
            '==',
            this.security.currentUserValue.id_vendedor
          )
          .where('possui_lembretes', '==', true)
          .orderBy('cpf')
          .limit(limit);

    return this.genericFireReqs.getCollection<Notas>(
      environment.DB_notas_clientes,
      true,
      queryFn
    );
  }

  getNotasCliente(
    clienteCpf: string,
    clienteSiglaMarca: string
  ): Observable<Notas> {
    return this.getNotas(clienteCpf, clienteSiglaMarca).pipe(
      map((notas) => {
        if (notas) {
          return notas;
        } else {
          return {} as Notas;
        }
      })
    );
  }

  private setNotas(
    clienteCpf: string,
    clienteSiglaMarca: string,
    notasDocument: Notas
  ): Observable<void> {
    notasDocument.possui_lembretes = notasDocument.notas.some(
      (nota) => nota.concluido == false
    );

    return this.genericFireReqs.setDocument<Notas>(
      environment.DB_notas_clientes,
      `${clienteSiglaMarca}_${clienteCpf}:base`,
      notasDocument
    );
  }

  setNota(
    clienteCpf: string,
    clienteSiglaMarca: string,
    text: string,
    notaType: 'ANOTACAO' | 'LEMBRETE',
    cliente: Cliente
  ): Observable<void> {
    return this.getVendedorInfoByEmail(
      this.security.currentUserValue.email
    ).pipe(
      switchMap((info) => {
        return this.getNotas(clienteCpf, clienteSiglaMarca, true).pipe(
          switchMap((notas) => {
            const newNota = {
              id_vendedor_escritor: this.security.currentUserValue.id_vendedor,
              nome_vendedor_escritor: info?.nome_personalizado
                ? info?.nome_personalizado
                : info?.nome,
              loja_vendedor_escritor: info?.loja,
              texto: text,
              data: new Date(),
              concluido: notaType == 'ANOTACAO' ? true : false,
            };

            const notasDocument: Notas = {
              cpf: clienteCpf,
              id_vendedor_preferencia: cliente.id_vendedor_preferencia,
              nome: cliente.nome,
              sigla_marca: clienteSiglaMarca,
              notas: notas?.notas ? [...notas?.notas, newNota] : [newNota],
            };

            return this.setNotas(clienteCpf, clienteSiglaMarca, notasDocument);
          })
        );
      })
    );
  }

  deleteNota(
    clienteCpf: string,
    clienteSiglaMarca: string,
    notaIdInArr: number
  ): Observable<void> {
    return this.getNotas(clienteCpf, clienteSiglaMarca, true).pipe(
      switchMap((notas) => {
        notas.notas.splice(notaIdInArr, 1);
        return this.setNotas(clienteCpf, clienteSiglaMarca, notas);
      })
    );
  }

  concludeNote(
    clienteCpf: string,
    clienteSiglaMarca: string,
    notaIdInArr: number
  ): Observable<void> {
    return this.getNotas(clienteCpf, clienteSiglaMarca, true).pipe(
      switchMap((notas) => {
        return this.getCurrentVendedorInfo().pipe(
          switchMap((info) => {
            notas.notas[notaIdInArr].concluido = true;
            notas.notas[notaIdInArr].data_conclusao = new Date();
            notas.notas[notaIdInArr].id_vendedor_marcador = info.id_crm;
            notas.notas[notaIdInArr].nome_vendedor_marcador =
              info.nome_personalizado ? info.nome_personalizado : info.nome;

            return this.setNotas(clienteCpf, clienteSiglaMarca, notas);
          })
        );
      })
    );
  }

  getVendedorInfoByIdCrm(idCrm: number): Observable<VendedorInfo> {
    return this.genericFireReqs.getDocument<VendedorInfo>(
      environment.DB_info_vendedores,
      `${this.security.currentUserValue.sigla_marca}_${idCrm}`
    );
  }

  getMrtVendCrmById(idCrm: number): Observable<VendCRM> {
    return this.genericFireReqs.getDocument<VendCRM>(
      environment.DB_mrt_vendedores_crm,
      `${this.security.currentUserValue.sigla_marca}_${idCrm}`
    );
  }

  getLojaCrmById(lojaId): Observable<LojaDeduplicada> {
    const queryFn: QueryFn = (ref) =>
      ref
        .where('id_crm', '==', lojaId)
        .where('sigla_marca', '==', this.security.currentUserValue.sigla_marca);

    return this.genericFireReqs
      .getCollection<LojaDeduplicada>(
        environment.DB_lojas_deduplicadas,
        false,
        queryFn
      )
      .pipe(map((lojas) => lojas[0]));
  }

  getVendedorInfoByEmail(
    email: string,
    loading = true,
    getDataOnce = true
  ): Observable<VendedorInfo> {
    const queryFn: QueryFn = (ref) => ref.where('email', '==', email);
    return this.genericFireReqs
      .getCollection<VendedorInfo>(
        environment.DB_info_vendedores,
        getDataOnce,
        queryFn,
        loading
      )
      .pipe(map((infos) => infos[0]));
  }

  getCurrentVendedorInfo(
    loading = true,
    getDataOnce = true
  ): Observable<VendedorInfo> {
    return this.getVendedorInfoByEmail(
      this.security.currentUserValue.email,
      loading,
      getDataOnce
    );
  }

  getAtivacaoData(clienteCpf: string): Observable<Ativacao> {
    this.spinner.show();

    const queryFn: QueryFn = (ref) =>
      ref.where('cpf', '==', clienteCpf).limit(1);

    return this.firestore
      .collection(environment.DB_vendedores)
      .doc(this.security.currentUserValue.email)
      .collection<Ativacao>(environment.DB_ativacoes, queryFn)
      .valueChanges()
      .pipe(
        map((ativacoes) => ativacoes[0]),
        tap((res) => this.spinner.hide())
      );
  }

  getCurrentMarcaInfo(): Observable<Empresa> {
    return this.genericFireReqs.getDocument<Empresa>(
      environment.DB_empresas,
      this.security.currentUserValue.sigla_marca
    );
  }

  obterVendedorAtual(
    loading = true,
    getDataOnce = true
  ): Observable<VendedorInfo | null> {
    const email = this.security.currentUserValue.email;

    // Se já carregou antes, retorna o valor armazenado
    if (this.vendedorAtualSubject.value && this.vendedorAtualSubject.value.id_crm == this.security.currentUserValue.id_vendedor) {
      console.log("VENDEDOR ATUAL FIREBASE JÁ EXISTENTE")
      return this.vendedorAtual$;
    }
    console.log("VENDEDOR ATUAL NÃO EXISTE OU NÃO É IGUAL AO USUÁRIO ATUAL, CARREGANDO OUTRO")
    return this.buscarVendedorPorEmail(email, loading, getDataOnce).pipe(
      tap((info) => this.vendedorAtualSubject.next(info)), // Armazena no BehaviorSubject
      shareReplay(1) // Evita chamadas duplicadas
    );
  }

  buscarVendedorPorEmail(
    email: string,
    loading = true,
    getDataOnce = true
  ): Observable<VendedorInfo> {
    const queryFn: any = (ref: any) => ref.where('email', '==', email);
    return this.genericFireReqs
      .getCollection<VendedorInfo>(
        environment.DB_info_vendedores,
        getDataOnce,
        queryFn,
        loading
      )
      .pipe(map((infos) => infos[0])); // Retorna o primeiro resultado
  }
}
